import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, DatePicker } from '@hopskipdrive/react-components';
import { isValid } from 'date-fns';

import { MINIMUM_SEARCH_STRING_LENGTH } from '../../../constants/queryParams';
import { useTimezone } from '../../../hooks/useTimezone';
import { SearchNavBar } from '../../../shared/components/SearchNavBar';
import { colorPalette } from '../../../shared/components/variables';
import { RecordingsQueryParams } from '../../hooks/useRecordingsQuery';
import { getRecordingsDateRange } from '../../utils/dateRange';

const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '22px',
    backgroundColor: 'white',
    height: '40px',
  },
};

interface RecordingsFilterBarProps {
  queryParams: RecordingsQueryParams;
  onFilterChange: (filters: Partial<RecordingsQueryParams>) => void;
}

export const RecordingsFilterBar: React.FC<RecordingsFilterBarProps> = ({ queryParams, onFilterChange }) => {
  const [, setSearchParams] = useSearchParams();
  const { getStartOfDate, getEndOfDate, getDateInTimezone } = useTimezone();
  const [dateValue, setDateValue] = useState<Date | null>(() => {
    if (!queryParams.startDate) {
      return null;
    }

    const zoned = getDateInTimezone(queryParams.startDate);

    return new Date(zoned.getFullYear(), zoned.getMonth(), zoned.getDate());
  });

  useEffect(() => {
    if (!queryParams.startDate) {
      setDateValue(null);
    }
  }, [queryParams.startDate]);

  const syncUrl = (key: string, value: string | null) => {
    setSearchParams((prev) => {
      if (value) {
        prev.set(key, value);
      } else {
        prev.delete(key);
      }

      return prev;
    });
  };

  const handleDateAccept = (date: Date | null) => {
    if (date && isValid(date)) {
      const { startDate, endDate } = getRecordingsDateRange(date, getStartOfDate, getEndOfDate);

      onFilterChange({ startDate, endDate });
      syncUrl('fromDate', startDate);
    } else {
      setDateValue(null);
      onFilterChange({ startDate: undefined, endDate: undefined });
      syncUrl('fromDate', null);
    }
  };

  const handleSearchQuery = (query: string) => {
    if (query.length > 0 && query.length < MINIMUM_SEARCH_STRING_LENGTH) {
      return;
    }

    const value = query || undefined;

    onFilterChange({ riderName: value });
    syncUrl('riderName', value ?? null);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '28px',
        px: '24px',
        py: '16px',
        backgroundColor: colorPalette.navy95,
        borderRadius: '5px',
        boxShadow: '0px 4px 10px 0px rgba(29, 75, 122, 0.1)',
        mb: '12px',
      }}
    >
      <Box display="flex" alignItems="center" gap="16px" flexShrink={0}>
        <Box
          component="label"
          sx={{
            color: colorPalette.neutralVariant60,
            fontSize: '13px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}
        >
          Jump To
        </Box>
        <DatePicker
          value={dateValue}
          onChange={(date) => {
            setDateValue(date);
            if (!date) {
              handleDateAccept(null);
            }
          }}
          onAccept={handleDateAccept}
          slotProps={{
            field: { clearable: true },
            textField: {
              placeholder: 'MM/DD/YYYY',
              size: 'small',
              inputProps: { 'data-testid': 'recordings-date-filter' },
              sx: { width: '180px', ...INPUT_SX },
            },
          }}
        />
      </Box>

      <SearchNavBar
        debounceHandleQuery={handleSearchQuery}
        placeholder="Search Riders"
        query={queryParams.riderName ?? ''}
      />
    </Box>
  );
};
